import {z} from 'zod';
import {LabelValueSchema} from '@netx/core-schema';

export enum DownloadOptionType {
	ORIGINAL = 'original',
	PROXY = 'proxy',
	ASSET_VIEW = 'assetView',
	ASSET_VERSION = 'assetVersion',
	PRESET_PROCESS = 'presetProcess',
	MANUAL_REPURPOSE = 'manualRepurpose',
	METADATA = 'metadata',
	HISTORICAL = 'historical',
	CONTACT_SHEET = 'contactSheet',
	TEMPLATE_CUSTOMIZATION = 'templateCustomization',
}

export enum DownloadOptionOptionLabel {
	METADATA_MAPPING = 'metadataMapping',
	SORT_FIELD = 'assetListSortField',
	SORT_ORDER = 'assetListSortOrder',
	TITLE = 'title',
}

export enum DownloadOptionName {
	// proxies
	THUMB = 'thumb',
	PREVIEW = 'preview',
	ZOOM = 'zoom',
	// metadata
	METADATA = 'metadata',
	// contact sheet
	CONTACT_SHEET_1_x_1 = '1x1',
	CONTACT_SHEET_2_x_2 = '2x2',
	CONTACT_SHEET_3_x_3 = '3x3',
}

export enum DownloadOptionOrganizeContentsBy {
	NONE = 0,
	FILE_FORMAT_FAMILY = -106,
	FILE_TYPE = -105,
	FOLDER = -108,
}

export const DownloadOptionSchema = z.object({
	/**
	 * could be:
	 *  - preset process id
	 *  - metadata attribute feildset id
	 *  - version id
	 *  - if contact sheet, `0` for defaults
	 */
	id: z.number().int().gt(-1, 'Invalid `id`'),
	/**
	 * applies only to `DownloadOptionType.CONTACT_SHEET` and `DownloadOptionType.METADATA`\
	 * specify a list of attribute templates to inlcude
	 */
	attributeIds: z.array(z.number().int()),
	/**
	 * applies only to `DownloadOptionType.PRESET_PROCESS` and `DownloadOptionType.MANUAL_REPURPOSE`\
	 * define the scope of the preset to a format family
	 */
	family: z.string(), // <empty string>, audio, document, image, video
	/**
	 * could be:
	 *  - asset view name
	 *  - preset process name
	 *  - IDS templateName (contact sheet)
	 *  - Java template name (contact sheet)
	 *  - `DownloadOptionName`
	 */
	name: z.string(),
	/**
	 * TODO: this is only referenced in setting a Registry.idService().getIntId("token_link_option")\
	 * not something the UI needs to set or know about as best I can tell - just keep setting it to 0
	 */
	optionId: z.number().int().gt(-1, 'Invalid `optionId`'),
	/**
	 * could be:
	 * for `DownloadOptionType.CONTACT_SHEET`\
	 * non IDS (java) contact sheet options:
	 *  - `assetListSortField`: field to sort assets on
	 *  - `assetListSortOrder`: sort order
	 *  - `displayLabels`: display attribute labels
	 *  - `highRes`: use high res images
	 *  - `title`: the title of the contact sheet
	 * @example
	 * ```json
	 * [
	 * 	{"label": "displayLabels", "value": true},
	 * 	{"label": "highRes", "value": false},
	 * 	{"label": "title", "value": "Lorem"},
	 * 	{"label": "assetListSortField", "value": "fileName"},
	 * 	{"label": "assetListSortOrder", "value": "0"}
	 * ]
	 * ```
	 *
	 * IDS contact sheet options:
	 *  - `assetListSortField`: field to sort assets on
	 *  - `assetListSortOrder`: asset sort order
	 *  - `highRes`: defaulted to `true`
	 *  - `pdfExportPreset`: a string value of the preset to use (from PDF config call)
	 *  - `templateName`: the name of the IDS template to use
	 *  - `title`: the title of the contact sheet
	 * @example
	 * ```json
	 * [
	 * 	{"label": "highRes", "value": true},
	 * 	{"label": "pdfExportPreset", "value": "Smallest File Size"},
	 * 	{"label": "templateName", "value": "a_new_PDF"},
	 * 	{"label": "title", "value": "Lorem"},
	 * 	{"label": "assetListSortField", "value": "fileName"},
	 * 	{"label": "assetListSortOrder", "value": "0"},
	 * ]
	 * ```
	 */
	options: z.array(LabelValueSchema),
	/**
	 * recursive download zip origanization (not exposed currently)\
	 * for assets (derivatives and proxies, but not metadata or contact sheets)
	 */
	organizeContentsBy: z.nativeEnum(DownloadOptionOrganizeContentsBy),
	/**
	 * whether or not this option is subject to approval\
	 * (read-only)
	 */
	requiresApproval: z.boolean(),
	/**
	 * whether or not this option requires justification\
	 * (read-only)
	 */
	requiresJustification: z.boolean(),
	type: z.nativeEnum(DownloadOptionType),
});

export default DownloadOptionSchema;

export type IDownloadOption = z.infer<typeof DownloadOptionSchema>;

/** all fields */
export const DownloadOptionField = DownloadOptionSchema.keyof().enum;

export const DownloadOptionsFormSchema = z.object({
	includeSubFolders: z.boolean(),
	downloadOptions: z.object({
		manualRepurpose: z.boolean(),
		assetVersion: z.record(z.boolean()).optional(),
		assetView: z.record(z.boolean()),
		contactSheet: z.object({
			engine: z.string(),
			enabled: z.boolean(),
			attributeIds: z.array(z.number()),
			name: z.string(),
			options: z.object({
				title: z.string(),
				pdfExportPreset: z.string(),
				displayLabels: z.boolean(),
				highRes: z.boolean(),
			}),
		}),
		original: z.boolean(),
		presetProcess: z.array(z.boolean()).or(z.record(z.boolean())).nullable(),
		proxy: z.record(z.boolean()),
		metadata: z.object({
			enabled: z.boolean(),
			attributeIds: z.array(z.number()),
		}),
	}),
});

export type IDownloadOptionsFormValues = z.infer<
	typeof DownloadOptionsFormSchema
>;
